import { useEffect, useCallback } from 'react'
import { useDashboardStore } from '../store'
import { checkHealth as pingHealth, fetchDrones, fetchViolations } from '../api'

const POLL_INTERVAL_MS = 5000
const HEALTH_POLL_INTERVAL_MS = 30000

export function useDashboard() {
  const {
    setDrones,
    setViolations,
    setApiStatus,
    setLastUpdate,
    setDroneStatus,
    setViolationStatus,
  } = useDashboardStore()

  const refreshDrones = useCallback(async () => {
    setDroneStatus('Updating...')
    try {
      const droneData = await fetchDrones()
      setDrones(droneData)
      setLastUpdate()
      setDroneStatus(`Synced (${droneData.length} drones)`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      setDroneStatus(`Error: ${message}`)
    }
  }, [setDrones, setLastUpdate, setDroneStatus])

  const refreshViolations = useCallback(async () => {
    setViolationStatus('Updating...')
    try {
      const violationData = await fetchViolations()
      setViolations(violationData)
      setViolationStatus(`Synced (${violationData.length} violations)`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      setViolationStatus(`Error: ${message}`)
    }
  }, [setViolations, setViolationStatus])

  const refreshHealth = useCallback(async () => {
    try {
      const healthy = await pingHealth()
      setApiStatus(healthy ? 'online' : 'offline')
    } catch {
      setApiStatus('offline')
    }
  }, [setApiStatus])

  useEffect(() => {
    // Initial boot
    const bootTimer = setTimeout(() => {
      refreshDrones()
      refreshViolations()
      refreshHealth()
    }, 100)

    // Poll drones and violations
    const interval = setInterval(() => {
      refreshDrones()
      refreshViolations()
    }, POLL_INTERVAL_MS)

    // Poll health less frequently
    const healthInterval = setInterval(() => {
      refreshHealth()
    }, HEALTH_POLL_INTERVAL_MS)

    return () => {
      clearTimeout(bootTimer)
      clearInterval(interval)
      clearInterval(healthInterval)
    }
  }, [refreshDrones, refreshViolations, refreshHealth])

  return {
    refreshDrones,
    refreshViolations,
    refreshHealth,
  }
}
